import { useState } from 'react';
import { Edit2, Trash2, Building2 } from 'lucide-react';
import { Button } from '../ui/button';

// Car/Vehicle Item Card Component
export const CarItemCard = ({ 
  item, 
  language, 
  isDarkMode, 
  onEdit 
}) => {
  return (
    <div className="space-y-2">
      {/* Car Image */}
      {(item.image || item.images?.[0]) && (
        <img 
          src={item.images?.[0] || item.image} 
          alt={item.make + ' ' + item.model} 
          className="w-full h-40 object-cover rounded-lg"
        />
      )}
      
      {/* Car Name */}
      <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
        {item.make} {item.model}
      </h3>
      
      {/* Car Details Grid */}
      <div className={`grid grid-cols-2 gap-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {item.year && (
          <div className="flex items-center gap-1">
            <span className="text-gray-500">📅</span>
            <span>{item.year}</span>
          </div>
        )}
        {item.mileage && (
          <div className="flex items-center gap-1">
            <span className="text-gray-500">🛣️</span>
            <span>{item.mileage?.toLocaleString()} km</span>
          </div>
        )}
        {item.color && (
          <div className="flex items-center gap-1">
            <span className="text-gray-500">🎨</span>
            <span>{item.color}</span>
          </div>
        )}
        {item.condition && (
          <div className="flex items-center gap-1">
            <span className="text-gray-500">⭐</span>
            <span>
              {item.condition === 'new' 
                ? (language === 'ar' ? 'جديد' : 'New') 
                : (language === 'ar' ? 'مستعمل' : 'Used')}
            </span>
          </div>
        )}
      </div>
      
      {/* Direct Edit Button */}
      <Button
        size="sm"
        className="w-full mt-2 bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
        onClick={(e) => {
          e.stopPropagation();
          onEdit(item);
        }}
      >
        <Edit2 className="h-4 w-4 me-2" />
        {language === 'ar' ? 'تعديل' : 'Edit'}
      </Button>
    </div>
  );
};

// Real Estate Item Card Component
export const RealEstateItemCard = ({ 
  item, 
  language, 
  isDarkMode, 
  onEdit,
  onDelete
}) => {
  const [imgError, setImgError] = useState(false);
  const imgSrc = item.images?.[0] || item.image;
  // Guard against corrupted/empty base64 data URIs
  const hasValidImage = !!imgSrc && !imgError && !(typeof imgSrc === 'string' && imgSrc.startsWith('data:') && imgSrc.length < 100);

  const getPropertyTypeLabel = (type) => {
    const labels = {
      apartment: { ar: 'شقة', en: 'Apartment' },
      villa: { ar: 'فيلا', en: 'Villa' },
      office: { ar: 'مكتب', en: 'Office' },
      land: { ar: 'أرض', en: 'Land' },
      townhouse: { ar: 'تاون هاوس', en: 'Townhouse' },
      penthouse: { ar: 'بنتهاوس', en: 'Penthouse' },
      studio: { ar: 'استوديو', en: 'Studio' },
      building: { ar: 'مبنى', en: 'Building' },
      commercial: { ar: 'محل تجاري', en: 'Commercial' },
      warehouse: { ar: 'مستودع', en: 'Warehouse' }
    };
    return labels[type]?.[language === 'ar' ? 'ar' : 'en'] || type;
  };

  const getListingTypeStyle = (type) => {
    const styles = {
      for_sale: { bg: 'bg-green-100 text-green-700', label: language === 'ar' ? 'للبيع' : 'Sale' },
      for_rent: { bg: 'bg-blue-100 text-blue-700', label: language === 'ar' ? 'للإيجار' : 'Rent' },
      for_investment: { bg: 'bg-amber-100 text-amber-700', label: language === 'ar' ? 'للاستثمار' : 'Investment' }
    };
    return styles[type] || styles.for_sale;
  };

  const dash = '—';

  return (
    <div className="flex flex-col space-y-2">
      {/* Property Image — fixed height, placeholder when missing/broken */}
      <div className={`w-full h-40 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center ${
        isDarkMode ? 'bg-white/5' : 'bg-gray-100'
      }`}>
        {hasValidImage ? (
          <img 
            src={imgSrc} 
            alt={item.title || 'property'} 
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="flex flex-col items-center gap-1 text-gray-400">
            <Building2 className="h-10 w-10" />
            <span className="text-xs">{language === 'ar' ? 'لا توجد صورة' : 'No image'}</span>
          </div>
        )}
      </div>
      
      {/* Property Title — fixed single line */}
      <h3 
        className={`font-bold text-lg truncate ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}
        title={item.title || ''}
      >
        {item.title || dash}
      </h3>
      
      {/* Property Details Grid — always 4 fixed slots */}
      <div className={`grid grid-cols-2 gap-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        <div className="flex items-center gap-1">
          <span className="text-gray-500">🏠</span>
          <span className="truncate">{item.propertyType ? getPropertyTypeLabel(item.propertyType) : dash}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-gray-500">🛏️</span>
          <span>{item.bedrooms ? `${item.bedrooms} ${language === 'ar' ? 'غرف' : 'BR'}` : dash}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-gray-500">📐</span>
          <span>{item.area ? `${item.area} sqft` : dash}</span>
        </div>
        <div className="flex items-center gap-1">
          {item.listingType ? (
            <span className={`px-2 py-0.5 rounded text-xs ${getListingTypeStyle(item.listingType).bg}`}>
              {getListingTypeStyle(item.listingType).label}
            </span>
          ) : (
            <span>{dash}</span>
          )}
        </div>
      </div>
      
      {/* Edit + Delete buttons */}
      <div className="flex gap-2 mt-2">
        <Button
          size="sm"
          className="flex-1 bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(item);
          }}
          data-testid={`realestate-card-edit-${item.id}`}
        >
          <Edit2 className="h-4 w-4 me-2" />
          {language === 'ar' ? 'تعديل' : 'Edit'}
        </Button>
        {onDelete && (
          <Button
            size="sm"
            variant="outline"
            className="text-red-600 border-red-300 hover:bg-red-50"
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(language === 'ar' ? 'حذف هذا العقار نهائياً؟' : 'Delete this property permanently?')) {
                onDelete(item.id);
              }
            }}
            data-testid={`realestate-card-delete-${item.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Phone Number Item Card Component  
export const PhoneItemCard = ({ 
  item, 
  language, 
  isDarkMode, 
  telecomLogos,
  onEdit 
}) => {
  // Format phone number for display
  const formatPhoneNumber = (number) => {
    if (!number) return '';
    // Remove any non-digit characters
    const cleaned = number.replace(/\D/g, '');
    // Format based on length
    if (cleaned.length === 10) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
    }
    return number;
  };

  const getCategoryStyle = (category) => {
    const styles = {
      vip: { bg: 'bg-gradient-to-r from-amber-500 to-yellow-500', text: 'text-white', label: 'VIP' },
      golden: { bg: 'bg-amber-100', text: 'text-amber-800', label: language === 'ar' ? 'ذهبي' : 'Golden' },
      silver: { bg: 'bg-gray-200', text: 'text-gray-700', label: language === 'ar' ? 'فضي' : 'Silver' },
      normal: { bg: 'bg-blue-100', text: 'text-blue-700', label: language === 'ar' ? 'عادي' : 'Normal' }
    };
    return styles[category] || styles.normal;
  };

  return (
    <div className="space-y-3">
      {/* Operator Logo */}
      {item.operator && telecomLogos?.[item.operator] && (
        <div className="flex justify-center">
          <img 
            src={telecomLogos[item.operator]} 
            alt={item.operator} 
            className="h-8 object-contain"
          />
        </div>
      )}
      
      {/* Phone Number Display */}
      <div className={`text-center py-3 px-4 rounded-xl ${
        isDarkMode ? 'bg-white/5' : 'bg-gray-50'
      }`}>
        <p className={`text-2xl font-bold tracking-wider ${
          isDarkMode ? 'text-white' : 'text-[#0F172A]'
        }`} dir="ltr">
          {formatPhoneNumber(item.number)}
        </p>
      </div>
      
      {/* Category Badge */}
      {item.category && (
        <div className="flex justify-center">
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${getCategoryStyle(item.category).bg} ${getCategoryStyle(item.category).text}`}>
            {getCategoryStyle(item.category).label}
          </span>
        </div>
      )}
      
      {/* Direct Edit Button */}
      <Button
        size="sm"
        className="w-full mt-2 bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
        onClick={(e) => {
          e.stopPropagation();
          onEdit(item);
        }}
      >
        <Edit2 className="h-4 w-4 me-2" />
        {language === 'ar' ? 'تعديل' : 'Edit'}
      </Button>
    </div>
  );
};

// Generic Item Card (for E-commerce and other types)
export const GenericItemCard = ({ 
  item, 
  language, 
  isDarkMode,
  onEdit,
  showEditButton = false
}) => {
  return (
    <>
      {item.image && (
        <img 
          src={item.image} 
          alt={item.name || item.title || item.number} 
          className="w-full h-40 object-cover rounded-lg mb-3" 
        />
      )}
      <h3 className={`font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
        {item.name || item.title || item.number || item.plateNumber || (item.make + ' ' + item.model)}
      </h3>
      
      {showEditButton && onEdit && (
        <Button
          size="sm"
          className="w-full mt-2 bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(item);
          }}
        >
          <Edit2 className="h-4 w-4 me-2" />
          {language === 'ar' ? 'تعديل' : 'Edit'}
        </Button>
      )}
    </>
  );
};

// Item Price Display Component
export const ItemPriceDisplay = ({ 
  item, 
  isDarkMode, 
  formatPrice,
  language 
}) => {
  if (item.hasDiscount && item.salePrice) {
    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded">
            -{item.discountPercentage || Math.round((1 - item.salePrice / item.price) * 100)}%
          </span>
          <span className={`text-sm line-through ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {formatPrice(item.price)}
          </span>
        </div>
        <span className={`font-bold text-lg ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
          {formatPrice(item.salePrice)}
        </span>
      </div>
    );
  }

  // Regular price (rent or sale)
  const price = item.rent_price || item.price || item.investment_price;
  const priceLabel = item.rent_price 
    ? (language === 'ar' ? '/سنة' : '/year')
    : '';

  return (
    <div className="flex items-center justify-end gap-1">
      <span className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
        {formatPrice(price)}
      </span>
      {priceLabel && (
        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {priceLabel}
        </span>
      )}
    </div>
  );
};
